import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Download, Users, TrendingUp, CheckCircle2, Clock, XCircle, Inbox } from "lucide-react";

const LEAD_STATUSES = ["new", "contacted", "pending", "closed", "converted"];
const SS = { new: "bg-sky-100 text-sky-700", contacted: "bg-violet-100 text-violet-700", pending: "bg-amber-100 text-amber-700", closed: "bg-slate-100 text-slate-600", converted: "bg-emerald-100 text-emerald-700" };

export default function AdminLeads() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ status: "all", city: "", category: "", date_from: "", date_to: "" });
  const role = localStorage.getItem("partner_role");

  const load = useCallback(async () => {
    setData(null);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    try { const { data } = await papi.get("/partner/manage/leads", { params }); setData(data); }
    catch (e) { if ([401, 403].includes(e.response?.status)) navigate("/login"); else toast.error(formatErr(e.response?.data?.detail)); }
  }, [f, navigate]);

  useEffect(() => { if (!["manager", "admin"].includes(role)) { navigate("/login"); return; } load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { await papi.put(`/partner/manage/leads/${id}/status`, { status }); load(); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const exportXlsx = async () => {
    try {
      const res = await papi.get("/partner/manage/leads/export.xlsx", { params: f.status !== "all" ? { status: f.status } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = "nirmantra_leads.xlsx"; a.click(); URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const stats = data ? [
    { icon: Inbox, label: "Total", value: data.total }, { icon: TrendingUp, label: "New", value: data.new },
    { icon: Users, label: "Contacted", value: data.contacted }, { icon: Clock, label: "Pending", value: data.pending },
    { icon: CheckCircle2, label: "Converted", value: data.converted }, { icon: XCircle, label: "Closed", value: data.closed },
  ] : [];

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div><h1 className="font-display text-2xl font-extrabold text-slate-900">Lead Dashboard</h1><p className="text-sm text-slate-500">All customer contact events across the partner network.</p></div>
          <Button data-testid="leads-export" onClick={exportXlsx} className="rounded-xl bg-emerald-600 hover:bg-emerald-700"><Download className="h-4 w-4 mr-1.5" /> Export Excel</Button>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {stats.map((s) => (
              <div key={s.label} className="card-premium rounded-xl p-4"><s.icon className="h-4 w-4 text-[#0E9AC8] mb-1.5" /><p className="font-display text-2xl font-extrabold text-slate-900">{s.value}</p><p className="text-xs text-slate-500">{s.label}</p></div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="leads-filter-status" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="all">All statuses</option>{LEAD_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <Input data-testid="leads-filter-city" className="rounded-xl w-36" placeholder="City" value={f.city} onChange={(e) => set("city", e.target.value)} />
          <Input data-testid="leads-filter-category" className="rounded-xl w-44" placeholder="Category" value={f.category} onChange={(e) => set("category", e.target.value)} />
          <Input data-testid="leads-filter-from" type="date" className="rounded-xl w-40" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} />
          <Input data-testid="leads-filter-to" type="date" className="rounded-xl w-40" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} />
        </div>

        {!data ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div> : (
          <div className="card-premium rounded-2xl overflow-hidden">
            {data.leads.length === 0 ? <div className="p-16 text-center text-slate-500"><Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />No leads yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                    <th className="text-left p-3">Vendor</th><th className="text-left p-3">Category</th><th className="text-left p-3">City</th>
                    <th className="text-left p-3">Customer</th><th className="text-left p-3">Requirement</th><th className="text-left p-3">Date</th><th className="text-left p-3">Status</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.leads.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-slate-50">
                        <td className="p-3 font-medium text-slate-800">{l.vendor_name}</td>
                        <td className="p-3 text-slate-600">{l.category}</td>
                        <td className="p-3 text-slate-600">{l.city}</td>
                        <td className="p-3 text-slate-600">{l.customer_name}<div className="text-xs text-slate-400">{l.customer_mobile}</div></td>
                        <td className="p-3 text-slate-600 max-w-[240px]"><div className="line-clamp-2">{l.project_requirement}</div></td>
                        <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{l.date}<br />{l.time}</td>
                        <td className="p-3">
                          <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className={`text-xs font-semibold px-2 py-1 rounded-lg capitalize border-0 ${SS[l.status]}`}>
                            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
